

import { useAuth } from "@/lib/auth";
import { CommunicationProvider, useCommunication } from "@/lib/CommunicationWrapper"
import { CLOUDFLARE_URL } from "@/lib/config";
import { X, Gift, ArrowRight, ArrowLeft, ChevronLeft, ChevronRight, ArrowLeftRight, CheckCircle2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Comparison } from "./Comparison";
import { useSearchParams } from "react-router-dom";
import { HighlightKey, highlights } from "./index";
import Confetti from 'react-confetti'

import startguyUrl from "../../../images/startguy.png"
import extlogoUrl from "../../../images/extlogo.png";
import { cn } from "@/lib/utils";

// Convert to absolute URLs for iframe with base tag
const startguy = new URL(startguyUrl, import.meta.url).href;
const extlogo = new URL(extlogoUrl, import.meta.url).href;

const Highlights = ({ highlight }: { highlight: string | null }) => {

    const highlightData = highlights[highlight];
    const keywords = highlightData?.keywords;

    if (keywords) {
        return <p className="text-sm text-gray-700">Go wild with <strong className="text-gray-800">{keywords}</strong> plus all other Learn+ features!</p>
    }
    return <p className="text-sm text-gray-700">Go wild with all Learn+ features!</p>
}

// Features data for slideshow
const features = [
    {
        title: "Study guides from files",
        description: "Generate interactive study guides from your course materials",
        image: "/feature-placeholder-3.png"
    },
    {
        title: "Auto notes from lectures",
        description: "Transcribe your lectures and generate notes from them",
        image: "/feature-placeholder-1.png"
    },
    {
        title: "Smart assignment planning",
        description: "Plan and track your assignments with intelligent scheduling",
        image: "/feature-placeholder-2.png"
    },
    {
        title: "Still not convinced?",
        description: "View our comprehensive feature breakdown",
        image: null,
        isLink: true
    }
];

export const ClaimTrialContent = ({ highlight }: { highlight: string | null }) => {

    const { hasUsedTrial, planType } = useAuth();
    const { closeModal, sendToBackgroundScript, authenticatedFetch } = useCommunication();

    const prevPlanType = useRef(planType);

    const [loading, setLoading] = useState(false);
    const [showSlideshow, setShowSlideshow] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);

    const scrollToRef = useRef<HTMLDivElement>(null);

    // Get tier from highlight data
    const highlightData = highlight ? highlights[highlight as HighlightKey] : null;
    const tier = highlightData?.tier || "learn"; // Default to "learn" if no highlight

    const handleUpgrade = async () => {
        const priceKey = 'learn_annually'; // Default to yearly Learn+ plan

        setLoading(true);
        try {
            const origin = window.location.origin;

            const response = await authenticatedFetch(`${CLOUDFLARE_URL}/users/create-stripe-checkout`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: {
                    priceKey: priceKey,
                    successUrl: `${origin}/success?origin=${origin}`,
                    cancelUrl: `${origin}/cancel?origin=${origin}`,
                    origin: origin,
                    trial: !hasUsedTrial, // Request trial if user hasn't used it
                } as any,
            });

            if (!response.ok) {
                const error = await response.json();
                console.error("Failed to create checkout:", error);
                throw new Error(error.error || "Failed to create checkout session");
            }

            const data = await response.json();
            const checkoutUrl = data.message?.checkoutUrl;

            if (!checkoutUrl) {
                throw new Error("No checkout URL returned");
            }

            await sendToBackgroundScript({
                type: "OPEN_STRIPE",
                url: checkoutUrl,
                origin: origin
            });
        } catch (error) {
            console.error("Error creating Stripe checkout:", error);
            // TODO: Show error to user
        } finally {
            setLoading(false);
        }
    }

    const scrollToComparison = () => {
        if (scrollToRef.current) {
            scrollToRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }

    const handleShowFeatures = () => {
        setShowSlideshow(true);
        setCurrentSlide(0);
    }

    const handleNextSlide = () => {
        if (currentSlide < features.length - 1) {
            setCurrentSlide(currentSlide + 1);
        } else {
            // After last slide, go back to main page
            setShowSlideshow(false);
            setCurrentSlide(0);
        }
    }

    const handlePrevSlide = () => {
        if (currentSlide > 0) {
            setCurrentSlide(currentSlide - 1);
        }
    }

    const handleBackToMain = () => {
        setShowSlideshow(false);
        setCurrentSlide(0);
    }


    /*
    return (
        <div className="flex relative w-full h-full bg-white">
            <div className="absolute top-4 right-4 z-1">
                <button onClick={closeModal}><X className="size-4" /></button>
            </div>
            <div className="absolute top-6 left-8 flex items-center gap-2">
                <img src={"/extlogo.png"} className="size-4 object-cover z-4" />
                <h3 className="text-sm font-semibold text-black">BetterCampus</h3>
            </div>


            <div className="w-full h-full flex flex-col items-center justify-center">

                <h3 className="text-3xl font-bold text-black tracking-wide" style={{ fontFamily: "'Instrument Serif', serif" }}>Study guides from files</h3>
                <p className="text-gray-500 text-sm mb-4">Create study guides from files in your Canvas course.</p>
                <div className="flex items-center relative">
                    <img src={"/study-guides-from-files.png"} className="w-[300px] h-[250px] object-contain" />
                    <ArrowRight className="size-4 absolute top-1/2 -translate-y-1/2 -right-10" />
                </div>
            </div>

        </div>
    )

    */

    const highlightTitle = highlightData?.title ?? null;

    return (
        <div className="relative w-full h-full bg-white overflow-hidden">
            <div className="absolute top-4 right-4 z-10">
                <button onClick={closeModal}><X className="size-4" /></button>
            </div>
            <div className="absolute top-6 left-8 flex items-center gap-2 z-10">
                <img src={"/extlogo.png"} className="size-4 object-cover" />
                <h3 className="text-sm font-semibold text-black">BetterCampus</h3>
            </div>

            {/* Sliding Container */}
            <div
                className="flex w-full h-full transition-transform duration-500 ease-in-out"
                style={{ transform: showSlideshow ? 'translateX(-100%)' : 'translateX(0)' }}
            >
                {/* Main Content Panel - Always First */}
                <div className="w-full h-full flex items-center justify-center flex-shrink-0">
                    <div className="relative w-full overflow-hidden mb-8">
                        {/* Title */}
                        <div className="mb-0 text-center flex flex-col justify-center items-center">
                            <div className="flex flex-col gap-2 items-center mb-2">
                                <h1 className="text-3xl font-bold text-black flex gap-2 items-center tracking-wide" style={{ fontFamily: "'Instrument Serif', serif" }}>
                                    {!highlightTitle && <p>You found a  <span className={`underline decoration-wavy ${tier === "style" ? "decoration-blue-400" : "decoration-orange-600"} decoration-2 underline-offset-4`}>{tier === "style" ? "Style+" : "Learn+"}</span> feature</p>}
                                    {highlightTitle && <p>{highlightTitle} <span className={`underline decoration-wavy ${tier === "style" ? "decoration-blue-400" : "decoration-orange-600"} decoration-2 underline-offset-4`}>{tier === "style" ? "Style+" : "Learn+"}</span></p>}
                                </h1>
                                <p className="">{tier === "style" ? "Customize your Canvas with unlimited themes and personalization." : "Tools that help you plan tasks, study faster, and stay on track."}</p>
                            </div>
                        </div>

                        {/* Buttons */}
                        <div className="space-y-1 mt-2 flex flex-col items-center justify-center bg-white relative z-1">
                            {/* Primary CTA - Only show for Learn+ tier */}
                            {tier === "learn" && (
                                <>
                                    <button
                                        onClick={handleUpgrade}
                                        disabled={loading}
                                        className="w-1/2 py-3.5 px-4 rounded-xl font-semibold transition-all duration-200 mt-1 bg-brand-gradient text-white hover:brightness-95 shadow-lg shadow-blue-500/20 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {loading ? (
                                            <span className="flex items-center justify-center gap-2 text-sm">
                                                <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                                Processing...
                                            </span>
                                        ) : hasUsedTrial ? (
                                            <><span className="text-white">Upgrade Now</span></>
                                        ) : <div className="flex justify-center items-center gap-2"><Gift className="size-4 text-white" /> <span className="text-white text-md">Try free for 7 days</span></div>}
                                    </button>

                                    <p className="text-xs text-gray-500 mt-1">Takes less than 15 seconds. Cancel anytime.</p>
                                </>
                            )}

                            {/* Secondary Button - Show for both tiers, but make it primary for Style+ */}
                            <button
                                onClick={handleShowFeatures}
                                className={tier === "style"
                                    ? "w-1/2 py-3.5 px-4 rounded-xl font-semibold transition-all duration-200 mt-1 bg-brand-gradient text-white hover:brightness-95 shadow-lg shadow-blue-500/20 cursor-pointer"
                                    : "underline w-1/2 mx-auto text-center py-2.5 px-6 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors duration-200 hover:bg-gray-100/50 dark:hover:bg-gray-800/50 rounded-lg"
                                }
                            >
                                See features
                            </button>
                        </div>
                    </div>

                    <img src={"/upgrade_guy.png"} className="size-[170px] object-contain absolute bottom-[-40px]" />
                </div>

                {/* Slideshow Panel - Slides in from right */}
                <div className="w-full h-full flex items-center justify-center flex-shrink-0 absolute left-full pb-6 pt-20">
                    <div className="w-full h-full flex flex-col items-center justify-center px-8 relative">
                        {/* Back to main link */}

                        {/*<button
                            onClick={handleBackToMain}
                            className="absolute -top-4 left-8 text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                        >
                            <ArrowLeft className="size-3.5" />
                        
                        </button>
/*}
                        {/* Feature Title */}
                        <h2 className="text-3xl font-bold text-black mb-2 text-center tracking-wide" style={{ fontFamily: "'Instrument Serif', serif" }}>

                            {features[currentSlide].title}
                        </h2>
                        <p className="text-gray-600 mb-6 text-center max-w-md text-sm">
                            {features[currentSlide].description}
                        </p>

                        {/* Feature Image or Link */}
                        {features[currentSlide].isLink ? (
                            // Link slide
                            <div className="flex flex-col items-center justify-center gap-6 w-full max-w-md">
                                <a
                                    href="https://bettercampus.com/pricing"
                                    target="_blank"
                                    className="w-2/3 py-3.5 px-6 bg-brand-gradient text-white rounded-xl font-semibold text-orange-700 hover:text-orange-800 transition-all text-center flex items-center justify-center gap-2"
                                >
                                    View all features
                                    <ArrowRight className="size-4" />
                                </a>
                                <button
                                    onClick={handleBackToMain}
                                    className="text-sm text-gray-600 hover:text-gray-800 underline"
                                >
                                    or go back to start your trial
                                </button>
                            </div>
                        ) : (
                            // Regular feature slide with image
                            <div className="flex items-center justify-center gap-2 w-full">
                                <button
                                    onClick={handlePrevSlide}
                                    disabled={currentSlide === 0}
                                    className="p-2 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                    <ChevronLeft className="size-6" />
                                </button>
                                <div className="flex-1 relative h-50 bg-gray-100 rounded-lg flex items-center justify-center">
                                    <img
                                        src={features[currentSlide].image}
                                        alt={features[currentSlide].title}
                                        className="w-full h-full object-contain rounded-lg"
                                        onError={(e) => {
                                            // Fallback if image doesn't exist
                                            e.currentTarget.style.display = 'none';
                                        }}
                                    />
                                    <div className="text-gray-400 text-sm absolute">Feature Image</div>
                                </div>
                                <button
                                    onClick={handleNextSlide}
                                    disabled={currentSlide === features.length - 1}
                                    className="p-2 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                    <ChevronRight className="size-6" />
                                </button>
                            </div>
                        )}

                        {/* Navigation Dots */}
                        <div className="flex gap-2 mb-6 mt-4">
                            {features.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentSlide(index)}
                                    className={`w-2 h-2 rounded-full transition-all duration-200 ${index === currentSlide
                                        ? 'bg-orange-600 w-5'
                                        : 'bg-gray-300 hover:bg-gray-400'
                                        }`}
                                />
                            ))}
                        </div>


                    </div>
                </div>
            </div>
        </div>
    )
}

// Variation B: Bullet Points Version (for A/B testing)
export const ClaimTrialContentBulletPoints = ({ highlight }: { highlight: string | null }) => {

    const { hasUsedTrial, planType } = useAuth();
    const { closeModal, sendToBackgroundScript, authenticatedFetch, removeSharedComponent, loadSharedComponent } = useCommunication();

    const prevPlanType = useRef(null);
    const [upgradeSuccess, setUpgradeSuccess] = useState(false);
    const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight })
    const [loading, setLoading] = useState(false);

    // Get tier from highlight data
    const highlightData = highlight ? highlights[highlight as HighlightKey] : null;
    const tier = highlightData?.tier || "learn"; // Default to "learn" if no highlight

    const handleShowFeatures = () => {
        loadSharedComponent("UpgradeModal");
        removeSharedComponent("TrialClaimGate");
    }

    const handleUpgrade = async () => {
        const priceKey = 'learn_annually'; // Default to yearly Learn+ plan

        setLoading(true);
        try {
            const origin = window.location.origin;

            const response = await authenticatedFetch(`${CLOUDFLARE_URL}/users/create-stripe-checkout`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: {
                    priceKey: priceKey,
                    successUrl: `${origin}/success?origin=${origin}`,
                    cancelUrl: `${origin}/cancel?origin=${origin}`,
                    origin: origin,
                    trial: !hasUsedTrial,
                } as any,
            });

            if (!response.ok) {
                const error = await response.json();
                console.error("Failed to create checkout:", error);
                throw new Error(error.error || "Failed to create checkout session");
            }

            const data = await response.json();
            const checkoutUrl = data.message?.checkoutUrl;

            if (!checkoutUrl) {
                throw new Error("No checkout URL returned");
            }

            await sendToBackgroundScript({
                type: "OPEN_STRIPE",
                url: checkoutUrl,
                origin: origin
            });
        } catch (error) {
            console.error("Error creating Stripe checkout:", error);
        } finally {
            setLoading(false);
        }
    }

    const highlightTitle = highlightData?.title ?? null;

    const bulletFeatures = tier === "style" ? [
        { icon: "🎨", text: "Unlimited theme swaps" },
        { icon: "✨", text: "Save unlimited themes" },
        { icon: "🔄", text: "Auto-rotate your themes" },
    ] : [
        { icon: "📚", text: "Study guides from files" },
        { icon: "🎙️", text: "Auto notes from lectures" },
        { icon: "✅", text: "Smart assignment planning" }
    ];

    useEffect(() => {
        if (prevPlanType.current === "free" && planType !== "free") {
            setUpgradeSuccess(true);
        }
        prevPlanType.current = planType;
    }, [planType])

    useEffect(() => {
        const handleResize = () => {
            setWindowSize({ width: window.innerWidth, height: window.innerHeight });
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);


    if (upgradeSuccess) {
        return (
            <div className="flex flex-col h-full overflow-y-auto items-center justify-center min-h-screen bg-gradient-to-b from-orange-50/30 via-white to-white relative">
                <Confetti
                    width={windowSize.width}
                    height={windowSize.height}
                    recycle={false}
                    numberOfPieces={100}
                    gravity={0.2}
                    initialVelocityY={-15}
                    wind={0.02}
                    tweenDuration={500}
                    drawShape={(ctx) => {
                        // Draw confetti pieces with orange/pink gradient theme
                        const colors = ['#ff6b6b', '#ffa952', '#ff686c', '#ff8c42', '#ff6b9d', '#ff8c69', '#ffa07a', '#ffb347'];
                        const randomColor = colors[Math.floor(Math.random() * colors.length)];
                        ctx.fillStyle = randomColor;
                        ctx.beginPath();
                        ctx.arc(0, 0, 5, 0, 2 * Math.PI);
                        ctx.fill();
                    }}
                />
                <div className="px-10 pt-8 pb-12 text-center max-w-2xl mx-auto">
                    {/* Success Icon */}
                    <div className="mb-6 flex justify-center">
                        <div className="relative">
                            <div className="absolute inset-0 bg-orange-500/20 rounded-full blur-2xl animate-pulse" />
                            <CheckCircle2 className="relative size-16 text-green-500" strokeWidth={2} />
                        </div>
                    </div>

                    {/* Main Heading */}
                    <h1 className="text-3xl font-bold text-black mb-2 tracking-wide" style={{ fontFamily: "'Instrument Serif', serif" }}>
                        You've been upgraded!
                    </h1>

                    <p className="text-sm text-gray-600 mb-4">Now experience the full power of BetterCampus!</p>


                    {/* Success Guy Image - Larger and more prominent */}
                    <div className="mb-8 flex justify-center">
                        <div className="relative">
                            <div className="absolute inset-0 bg-gradient-to-br from-orange-200/40 to-pink-200/40 rounded-full blur-3xl -z-10" />
                            <img
                                src="/success_guy.png"
                                className="size-32 object-contain mx-auto drop-shadow-lg"
                                alt="Success celebration"
                            />
                        </div>
                    </div>

                    {/* Status Message */}
                    <div className="space-y-6">
                        <div>
                            <button
                                onClick={() => removeSharedComponent("TrialClaimGate")}
                                className="inline-flex items-center gap-2 px-8 py-3 bg-brand-gradient text-white font-semibold rounded-xl hover:brightness-95 transition-all duration-200 shadow-lg shadow-orange-500/20 hover:shadow-xl hover:shadow-orange-500/30"
                            >
                                Let's go!
                            </button>
                        </div>
                    </div>

                </div>
            </div>
        )
    }

    return (
        <div className="relative w-full h-full bg-white overflow-hidden">
            <button className="absolute top-4 right-4 size-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors" onClick={() => removeSharedComponent("TrialClaimGate")}><X className="size-4" /></button>

            <div className="absolute top-6 left-8 flex items-center gap-2 z-10">
                <img src={"/extlogo.png"} className="size-4 object-cover" />
                <h3 className="text-sm font-semibold text-black">BetterCampus</h3>
            </div>

            <div className="w-full h-full flex items-center justify-center">
                <div className="relative w-full overflow-hidden px-8">
                    {/* Title */}
                    <div className="text-center flex flex-col justify-center items-center">
                        <div className="flex flex-col gap-2 items-center mb-2">
                            <h1 className="text-3xl font-bold text-black flex gap-2 items-center tracking-wide" style={{ fontFamily: "'Instrument Serif', serif" }}>
                                {!highlightTitle && <p>You found a  <span className={`underline decoration-wavy ${tier === "style" ? "decoration-blue-400" : "decoration-orange-600"} decoration-2 underline-offset-4`}>{tier === "style" ? "Style+" : "Learn+"}</span> feature</p>}
                                {highlightTitle && <p>{highlightTitle} <span className={`underline decoration-wavy ${tier === "style" ? "decoration-blue-400" : "decoration-orange-600"} decoration-2 underline-offset-4`}>{tier === "style" ? "Style+" : "Learn+"}</span></p>}
                            </h1>
                            <p className="">{tier === "style" ? "Customize your Canvas with unlimited themes and personalization." : "Tools that help you plan tasks, study faster, and stay on track."}</p>
                        </div>
                    </div>

                    {/* Feature Bullets */}
                    <div className="max-w-md mx-auto my-2 flex justify-center">
                        <ul className="">
                            {bulletFeatures.map((feature, index) => (
                                <li key={index} className="flex items-center gap-3 text-gray-700 text-sm">
                                    <span className="flex-shrink-0">{feature.icon}</span>
                                    <span className="font-medium">{feature.text}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Buttons */}
                    <div className="space-y-1 mt-4 flex flex-col items-center justify-center bg-white relative z-1">
                        {/* Primary CTA - Only show for Learn+ tier */}
                        {tier === "learn" && (
                            <>
                                <button
                                    onClick={handleUpgrade}
                                    disabled={loading}
                                    className="w-1/2 py-3.5 px-4 rounded-xl font-semibold transition-all duration-200 mt-1 bg-brand-gradient text-white hover:brightness-95 shadow-lg shadow-blue-500/20 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? (
                                        <span className="flex items-center justify-center gap-2 text-sm">
                                            <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                            Processing...
                                        </span>
                                    ) : hasUsedTrial ? (
                                        <><span className="text-white">Upgrade Now</span></>
                                    ) : <div className="flex justify-center items-center gap-2"><Gift className="size-4 text-white" /> <span className="text-white text-md">Try free for 7 days</span></div>}
                                </button>

                                <p className="text-xs text-gray-500 mt-1">Takes less than 15 seconds. Cancel anytime.</p>
                            </>
                        )}

                        {/* Secondary Button - Show for both tiers, but make it primary for Style+ */}
                        <button
                            onClick={() => handleShowFeatures()}
                            className={tier === "style"
                                ? "w-1/2 py-3.5 px-4 rounded-xl font-semibold transition-all duration-200 mt-1 bg-brand-gradient text-white hover:brightness-95 shadow-lg shadow-blue-500/20 cursor-pointer"
                                : "underline w-1/2 mx-auto text-center py-2.5 px-6 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors duration-200 hover:bg-gray-100/50 dark:hover:bg-gray-800/50 rounded-lg"
                            }
                        >
                            See all features
                        </button>
                    </div>
                </div>

                <img src={tier === "style" ? "/style_guy.png" : "/upgrade_guy.png"} className="z-1 size-[200px] object-contain absolute -bottom-10 -right-8" />
            </div>
        </div>
    )
}

const UpgradeSuccessMessage = ({ windowSize, onClose }: { windowSize: { width: number, height: number }, onClose: () => void }) => {
    return (
        <div className="flex flex-col h-full overflow-y-auto items-center justify-center min-h-screen bg-gradient-to-b from-orange-50/30 via-white to-white relative">
            <Confetti
                width={windowSize.width}
                height={windowSize.height}
                recycle={false}
                numberOfPieces={100}
                gravity={0.2}
                initialVelocityY={-15}
                wind={0.02}
                tweenDuration={500}
                drawShape={(ctx) => {
                    // Draw confetti pieces with orange/pink gradient theme
                    const colors = ['#ff6b6b', '#ffa952', '#ff686c', '#ff8c42', '#ff6b9d', '#ff8c69', '#ffa07a', '#ffb347'];
                    const randomColor = colors[Math.floor(Math.random() * colors.length)];
                    ctx.fillStyle = randomColor;
                    ctx.beginPath();
                    ctx.arc(0, 0, 5, 0, 2 * Math.PI);
                    ctx.fill();
                }}
            />
            <div className="px-10 pt-8 pb-12 text-center max-w-2xl mx-auto">
                {/* Success Icon */}
                <div className="mb-6 flex justify-center">
                    <div className="relative">
                        <div className="absolute inset-0 bg-orange-500/20 rounded-full blur-2xl animate-pulse" />
                        <CheckCircle2 className="relative size-16 text-green-500" strokeWidth={2} />
                    </div>
                </div>

                {/* Main Heading */}
                <h1 className="text-3xl font-bold text-black mb-2 tracking-wide" style={{ fontFamily: "'Instrument Serif', serif" }}>
                    You've been upgraded!
                </h1>

                <p className="text-sm text-gray-600 mb-4">Now experience the full power of BetterCampus!</p>


                {/* Success Guy Image - Larger and more prominent */}
                <div className="mb-8 flex justify-center">
                    <div className="relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-orange-200/40 to-pink-200/40 rounded-full blur-3xl -z-10" />
                        <img
                            src="/success_guy.png"
                            className="size-32 object-contain mx-auto drop-shadow-lg"
                            alt="Success celebration"
                        />
                    </div>
                </div>

                {/* Status Message */}
                <div className="space-y-6">
                    <div>
                        <button
                            onClick={onClose}
                            className="inline-flex items-center gap-2 px-8 py-3 bg-brand-gradient text-white font-semibold rounded-xl hover:brightness-95 transition-all duration-200 shadow-lg shadow-orange-500/20 hover:shadow-xl hover:shadow-orange-500/30"
                        >
                            Let's go!
                        </button>
                    </div>
                </div>

            </div>
        </div>
    )
}

export const ClaimTrialButton = ({ className }: { className?: string }) => {
    const { hasUsedTrial, planType } = useAuth();
    const {
        sendToBackgroundScript,
        authenticatedFetch,
    } = useCommunication();

    const prevPlanType = useRef(null);
    const [upgradeSuccess, setUpgradeSuccess] = useState(false);
    const [upgradeClosed, setUpgradeClosed] = useState(false)
    const [loading, setLoading] = useState(false);

    const handleUpgrade = async () => {
        const priceKey = "learn_annually"; // Default to yearly Learn+ plan

        setLoading(true);
        try {
            const origin = window.location.origin;

            const response = await authenticatedFetch(
                `${CLOUDFLARE_URL}/users/create-stripe-checkout`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: {
                        priceKey: priceKey,
                        successUrl: `${origin}/success?origin=${origin}`,
                        cancelUrl: `${origin}/cancel?origin=${origin}`,
                        origin: origin,
                        trial: !hasUsedTrial,
                    } as any,
                }
            );

            if (!response.ok) {
                const error = await response.json();
                console.error("Failed to create checkout:", error);
                throw new Error(error.error || "Failed to create checkout session");
            }

            const data = await response.json();
            const checkoutUrl = data.message?.checkoutUrl;

            if (!checkoutUrl) {
                throw new Error("No checkout URL returned");
            }

            await sendToBackgroundScript({
                type: "OPEN_STRIPE",
                url: checkoutUrl,
                origin: origin,
            });
        } catch (error) {
            console.error("Error creating Stripe checkout:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (prevPlanType.current === "free" && planType !== "free") {
            setUpgradeSuccess(true);
        }
        prevPlanType.current = planType;
    }, [planType]);

    if (upgradeSuccess) return

    return (
        <button
            onClick={handleUpgrade}
            disabled={loading || upgradeSuccess}
            className={cn("py-3.5 px-4 rounded-xl font-semibold transition-all duration-200 mt-1 bg-brand-gradient text-white hover:brightness-95 shadow-lg shadow-blue-500/20 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed", className)}
        >
            {
                upgradeSuccess && !upgradeClosed && <UpgradeSuccessMessage windowSize={{
                    width: window.innerWidth,
                    height: window.innerHeight,
                }} onClose={() => setUpgradeClosed(true)} />
            }
            {loading ? (
                <span className="flex items-center justify-center gap-2 text-sm">
                    <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    Processing...
                </span>
            ) : hasUsedTrial ? (
                <><span className="text-white">Upgrade Now</span></>
            ) : upgradeSuccess ? "Upgraded!" : <div className="flex justify-center items-center gap-2"><Gift className="size-4 text-white" /> <span className="text-white text-md">Try free for 7 days</span></div>}
        </button>
    )
}


export const ClaimTrial = () => {

    const [searchParams] = useSearchParams();
    const highlight = searchParams.get("highlight");

    if (true) return <ClaimTrialBulletPoints />

    return (
        <ClaimTrialContent highlight={highlight} />
    )
}

// Export variation B for A/B testing
export const ClaimTrialBulletPoints = () => {

    const [searchParams] = useSearchParams();
    const highlight = searchParams.get("highlight");

    return (
        <ClaimTrialContentBulletPoints highlight={highlight} />
    )
}